<template>
  <div class="user-management">
    <!-- 页面标题和操作按钮 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">用户管理</h2>
        <p class="page-subtitle">管理平台所有注册用户</p>
      </div>
      <div class="header-right">
        <el-button type="success" @click="handleCreateUser" style="margin-right: 10px">
          <el-icon><Plus /></el-icon>
          新增用户
        </el-button>
        <el-button type="primary" @click="handleExportUsers">
          <el-icon><Download /></el-icon>
          导出用户
        </el-button>
      </div>
    </div>

    <!-- 筛选和搜索区域 -->
    <div class="filter-section">
      <el-card shadow="never" class="filter-card">
        <div class="filter-content">
          <!-- 快速筛选 -->
          <div class="quick-filters">
            <el-radio-group v-model="filterParams.status" @change="handleFilterChange">
              <el-radio-button label="">全部用户</el-radio-button>
              <el-radio-button label="active">活跃用户</el-radio-button>
              <el-radio-button label="inactive">未激活</el-radio-button>
              <el-radio-button label="banned">已封禁</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 高级筛选 -->
          <div class="advanced-filters">
            <el-form :model="filterParams" inline>
              <el-form-item label="会员类型">
                <el-select
                  v-model="filterParams.memberType"
                  placeholder="选择会员类型"
                  clearable
                  @change="handleFilterChange"
                >
                  <el-option label="普通会员" value="normal" />
                  <el-option label="高级会员" value="advanced" />
                  <el-option label="超级会员" value="premium" />
                </el-select>
              </el-form-item>

              <el-form-item label="注册时间">
                <el-date-picker
                  v-model="filterParams.registerDateRange"
                  type="daterange"
                  range-separator="至"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  @change="handleFilterChange"
                  value-format="YYYY-MM-DD"
                />
              </el-form-item>

              <el-form-item>
                <el-button @click="resetFilters">重置</el-button>
              </el-form-item>
            </el-form>
          </div>

          <!-- 搜索框 -->
          <div class="search-box">
            <el-input
              v-model="filterParams.keyword"
              placeholder="搜索用户昵称、手机号或邮箱"
              prefix-icon="Search"
              @input="handleSearch"
              clearable
              class="search-input"
            />
          </div>
        </div>
      </el-card>
    </div>

    <!-- 数据统计卡片 -->
    <div class="stats-section">
      <el-row :gutter="8">
        <el-col :xs="12" :sm="6" :md="4" v-for="stat in userStats" :key="stat.title">
          <el-card shadow="hover" class="stat-card">
            <div class="stat-content">
              <div class="stat-icon" :style="{ color: stat.color }">
                <el-icon><component :is="stat.icon" /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-title">{{ stat.title }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 用户列表 -->
    <div class="list-section">
      <el-card shadow="never">
        <template #header>
          <div class="table-header">
            <span>用户列表</span>
            <div class="table-actions">
              <el-tooltip content="刷新数据" placement="top">
                <el-button circle @click="loadUsers">
                  <el-icon><Refresh /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </template>

        <el-table
          :data="userList"
          v-loading="loading"
          stripe
          style="width: 100%"
          @sort-change="handleSortChange"
        >
          <el-table-column type="expand">
            <template #default="{ row }">
              <div class="expand-content">
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="用户ID">
                    {{ row.id }}
                  </el-descriptions-item>
                  <el-descriptions-item label="注册时间">
                    {{ formatDateTime(row.registerTime) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="微信号">
                    {{ row.wechat_number || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="微信OpenID">
                    {{ row.openid || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="用户余额">
                    {{ (row.balance || 0).toFixed(2) }} 元
                  </el-descriptions-item>
                  <el-descriptions-item label="最后登录">
                    {{ formatDateTime(row.lastLoginTime) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="登录次数">
                    {{ row.loginCount }}
                  </el-descriptions-item>
                </el-descriptions>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="用户信息" min-width="200">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :size="40" :src="row.avatar" />
                <div class="user-details">
                  <div class="user-name">{{ row.nickname }}</div>
                  <div class="user-contact">{{ row.phone || row.email }}</div>
                  <div class="user-wechat" v-if="row.wechat_number">微信号: {{ row.wechat_number }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="memberType" label="会员类型" width="100" sortable>
            <template #default="{ row }">
              <el-tag :type="getMemberTypeTag(row.memberType)">
                {{ getMemberTypeLabel(row.memberType) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="activityScore" label="活跃度" width="100" sortable>
            <template #default="{ row }">
              <el-progress 
                :percentage="row.activityScore" 
                :color="getActivityColor(row.activityScore)"
                :show-text="false"
              />
              <span class="score-text">{{ row.activityScore }}%</span>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="状态" width="100" sortable>
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" effect="light">
                {{ getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="createdActivities" label="创建活动" width="100" sortable />
          <el-table-column prop="joinedActivities" label="参与活动" width="100" sortable />

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleViewDetails(row)">
                详情
              </el-button>
              <el-dropdown>
                <el-button size="small">
                  更多
                  <el-icon><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="handleEditUser(row)">
                      <el-icon><Edit /></el-icon>
                      编辑
                    </el-dropdown-item>
                    <el-dropdown-item 
                      @click="handleToggleStatus(row)"
                      :disabled="row.status === 'banned'"
                    >
                      <el-icon><User /></el-icon>
                      {{ row.status === 'active' ? '禁用' : '启用' }}
                    </el-dropdown-item>
                    <el-dropdown-item 
                      @click="handleSendMessage(row)"
                    >
                      <el-icon><Message /></el-icon>
                      发送消息
                    </el-dropdown-item>
                    <el-dropdown-item divided @click="handleGrantTickets(row)">
                      <el-icon><Ticket /></el-icon>
                      赠送发起券
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.current"
            v-model:page-size="pagination.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 用户编辑弹窗 -->
    <user-dialog
      v-model="dialogVisible"
      :user="currentUser"
      :mode="dialogMode"
      @success="handleDialogSuccess"
    />

    <!-- 赠送单次发起券 -->
    <el-dialog v-model="ticketDialogVisible" title="赠送单次发起券" width="460px">
      <div v-if="ticketDialogUser">
        <p class="ticket-user-line">
          用户：<strong>{{ ticketDialogUser.nickname || ticketDialogUser.username || ticketDialogUser.id }}</strong>
          <span class="ticket-balance">当前持有 {{ ticketBalance }} 张</span>
        </p>
        <el-form label-width="90px">
          <el-form-item label="赠送数量">
            <el-input-number v-model="ticketForm.quantity" :min="1" :max="100" />
            <span class="ticket-tip">1 张 = 1 次额外发起活动机会</span>
          </el-form-item>
          <el-form-item label="备注">
            <el-input
              v-model="ticketForm.reason"
              type="textarea"
              :rows="2"
              maxlength="100"
              show-word-limit
              placeholder="可选，如：活动补偿 / 运营赠送"
            />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button @click="ticketDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="ticketSubmitting" @click="submitGrantTickets">确认赠送</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Medal, Ticket } from '@element-plus/icons-vue'
import UserDialog from '@/components/UserDialog.vue'
import api from '@/utils/api'

export default {
  name: 'UserManagement',
  components: {
    UserDialog,
    Medal,
    Ticket
  },
  
  setup() {
    const loading = ref(false)
    const userList = ref([])
    
    // 弹窗相关状态
    const dialogVisible = ref(false)
    const dialogMode = ref('create') // 'create' 或 'edit'
    const currentUser = ref(null)

    // 赠送单次发起券
    const ticketDialogVisible = ref(false)
    const ticketDialogUser = ref(null)
    const ticketBalance = ref(0)
    const ticketSubmitting = ref(false)
    const ticketForm = reactive({ quantity: 1, reason: '' })
    
    // 筛选参数
    const filterParams = reactive({
      status: '',
      memberType: '',
      registerDateRange: [],
      keyword: '',
      sortField: '',
      sortOrder: ''
    })
    
    // 分页参数
    const pagination = reactive({
      current: 1,
      size: 10,
      total: 0
    })
    
    // 用户统计
    const userStats = ref([
      {
        title: '总用户数',
        value: '0',
        icon: 'User',
        color: '#409EFF'
      },
      {
        title: '今日新增',
        value: '0',
        icon: 'UserFilled',
        color: '#67C23A'
      },
      {
        title: '活跃用户',
        value: '0',
        icon: 'TrendCharts',
        color: '#E6A23C'
      },
      {
        title: 'VIP用户',
        value: '0',
        icon: 'Star',
        color: '#F56C6C'
      },
      {
        title: '超级用户',
        value: '0',
        icon: 'Medal',
        color: '#9C27B0'
      }
    ])
    
    // 加载用户列表
    const loadUsers = async () => {
      loading.value = true
      try {
        // 构建查询参数（后端接受 limit 而非 pageSize）
        const params = {
          page: pagination.current,
          limit: pagination.size,
          status: filterParams.status,
          memberType: filterParams.memberType,
          keyword: filterParams.keyword,
          sortField: filterParams.sortField,
          sortOrder: filterParams.sortOrder
        }
        
        // 添加时间范围筛选
        if (filterParams.registerDateRange && filterParams.registerDateRange.length === 2) {
          params.startDate = filterParams.registerDateRange[0]
          params.endDate = filterParams.registerDateRange[1]
        }
        
        // 调用真实API
        const response = await api.getUsers(params)
        
        const users = response?.data?.users || []
        const paginationData = response?.data?.pagination || {}
        
        userList.value = users
        pagination.total = paginationData.total || 0
        
        // 调用用户统计API（全库统计：总用户数/今日新增/活跃用户/VIP/超级用户）
        let statsData = {}
        try {
          const statsResponse = await api.getUserStats()
          if (statsResponse && statsResponse.success) {
            statsData = statsResponse.data.stats || {}
          }
        } catch (statsError) {
          console.error('获取用户统计数据失败，回退为本地统计:', statsError)
        }
        
        // 更新统计数据（优先使用后端全库统计，失败时回退到当前页本地统计）
        userStats.value = [
          {
            title: '总用户数',
            value: (statsData.totalUsers ?? paginationData.total).toString(),
            icon: 'User',
            color: '#409EFF'
          },
          {
            title: '今日新增',
            value: (statsData.todayNewUsers ?? 0).toString(),
            icon: 'UserFilled',
            color: '#67C23A'
          },
          {
            title: '活跃用户',
            value: (statsData.activeUsers ?? 0).toString(),
            icon: 'TrendCharts',
            color: '#E6A23C'
          },
          {
            title: 'VIP用户',
            value: (statsData.vipUsers ?? users.filter(user => user.memberType === 'advanced').length).toString(),
            icon: 'Star',
            color: '#F56C6C'
          },
          {
            title: '超级用户',
            value: (statsData.premiumUsers ?? users.filter(user => user.memberType === 'premium').length).toString(),
            icon: 'Medal',
            color: '#9C27B0'
          }
        ]
        
      } catch (error) {
        console.error('加载用户列表失败:', error)
        ElMessage.error('加载用户列表失败')
      } finally {
        loading.value = false
      }
    }
    
    // 处理表格排序（点击列头排序，第三次点击清除排序）
    const handleSortChange = ({ prop, order }) => {
      if (order === 'ascending') {
        filterParams.sortField = prop
        filterParams.sortOrder = 'asc'
      } else if (order === 'descending') {
        filterParams.sortField = prop
        filterParams.sortOrder = 'desc'
      } else {
        filterParams.sortField = ''
        filterParams.sortOrder = ''
      }
      pagination.current = 1
      loadUsers()
    }
    
    // 处理筛选变化
    const handleFilterChange = () => {
      pagination.current = 1
      loadUsers()
    }
    
    // 处理搜索
    const handleSearch = () => {
      pagination.current = 1
      loadUsers()
    }
    
    // 重置筛选
    const resetFilters = () => {
      Object.assign(filterParams, {
        status: '',
        memberType: '',
        registerDateRange: [],
        keyword: ''
      })
      pagination.current = 1
      loadUsers()
    }
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pagination.size = size
      pagination.current = 1
      loadUsers()
    }
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      pagination.current = current
      loadUsers()
    }
    
    // 查看用户详情
    const handleViewDetails = (user) => {
      ElMessageBox.alert(`
        <div>
          <h3>${user.nickname}</h3>
          <p><strong>用户ID:</strong> ${user.id}</p>
          <p><strong>手机号:</strong> ${user.phone || '未设置'}</p>
          <p><strong>邮箱:</strong> ${user.email || '未设置'}</p>
          <p><strong>会员类型:</strong> ${getMemberTypeLabel(user.memberType)}</p>
          <p><strong>用户余额:</strong> ${(user.balance || 0).toFixed(2)} 元</p>
          <p><strong>状态:</strong> ${getStatusLabel(user.status)}</p>
          <p><strong>活跃度:</strong> ${user.activityScore || 0}%</p>
          <p><strong>创建活动:</strong> ${user.createdActivities || 0} 个</p>
          <p><strong>参与活动:</strong> ${user.joinedActivities || 0} 个</p>
          <p><strong>注册时间:</strong> ${formatDateTime(user.registerTime)}</p>
          <p><strong>最后登录:</strong> ${formatDateTime(user.lastLoginTime)}</p>
        </div>
      `, '用户详情', {
        dangerouslyUseHTMLString: true,
        customClass: 'user-detail-modal',
        confirmButtonText: '关闭'
      })
    }
    
    // 创建用户
    const handleCreateUser = () => {
      dialogMode.value = 'create'
      currentUser.value = null
      dialogVisible.value = true
    }
    
    // 编辑用户
    const handleEditUser = (user) => {
      dialogMode.value = 'edit'
      currentUser.value = { ...user }
      dialogVisible.value = true
    }
    
    // 处理弹窗成功
    const handleDialogSuccess = () => {
      loadUsers() // 重新加载用户数据
    }
    
    // 切换用户状态
    const handleToggleStatus = async (user) => {
      try {
        await api.toggleUserStatus(user.id)
        ElMessage.success('用户状态切换成功')
        loadUsers() // 重新加载数据
      } catch (error) {
        console.error('切换用户状态失败:', error)
        ElMessage.error('切换用户状态失败')
      }
    }
    
    // 发送消息
    const handleSendMessage = (user) => {
      ElMessage.info(`发送消息给: ${user.nickname}`)
    }

    // 打开“赠送单次发起券”弹窗
    const handleGrantTickets = async (user) => {
      ticketDialogUser.value = user
      ticketForm.quantity = 1
      ticketForm.reason = ''
      ticketBalance.value = 0
      ticketDialogVisible.value = true

      try {
        const res = await api.getUserActivityTickets(user.id)
        if (res.success) {
          ticketBalance.value = res.data?.count || 0
        }
      } catch (e) {
        // 查询余额失败不阻断赠送
        console.warn('查询用户发起券余额失败:', e)
      }
    }

    // 提交赠送
    const submitGrantTickets = async () => {
      if (!ticketDialogUser.value) return
      const quantity = parseInt(ticketForm.quantity, 10)
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
        ElMessage.warning('赠送数量需为 1-100 的整数')
        return
      }

      ticketSubmitting.value = true
      try {
        const res = await api.grantUserActivityTickets(ticketDialogUser.value.id, {
          quantity,
          reason: ticketForm.reason
        })
        if (res.success) {
          ElMessage.success(res.message || `已赠送 ${quantity} 张单次发起券`)
          ticketBalance.value = res.data?.count ?? (ticketBalance.value + quantity)
          ticketDialogVisible.value = false
          loadUsers()
        } else {
          ElMessage.error(res.message || '赠送失败')
        }
      } catch (error) {
        console.error('赠送发起券失败:', error)
        ElMessage.error(error.response?.data?.message || '赠送失败')
      } finally {
        ticketSubmitting.value = false
      }
    }
    
    // 导出用户
    const handleExportUsers = () => {
      ElMessage.info('导出用户数据')
    }
    
    // 工具函数
    const getMemberTypeLabel = (type) => {
        // 统一会员类型标签映射 - 仅包含三种类型
        const labels = {
          normal: '普通会员',
          advanced: '高级会员',
          premium: '超级会员'
        }
        return labels[type] || type
      }
    
    const getMemberTypeTag = (type) => {
        // 统一会员类型标签样式映射
        const tags = {
          normal: 'info',
          advanced: 'warning',
          premium: 'danger'
        }
        return tags[type] || 'default'
    }
    
    const getStatusLabel = (status) => {
      const labels = {
        active: '活跃',
        inactive: '未激活',
        banned: '已封禁'
      }
      return labels[status] || status
    }
    
    const getStatusTagType = (status) => {
      const types = {
        active: 'success',
        inactive: 'info',
        banned: 'danger'
      }
      return types[status] || ''
    }
    
    const getActivityColor = (score) => {
      if (score >= 80) return '#67c23a'
      if (score >= 60) return '#e6a23c'
      if (score >= 40) return '#409eff'
      return '#909399'
    }
    
    const formatDateTime = (datetime) => {
      if (!datetime) return ''
      return datetime.replace(' ', ' ')
    }
    
    onMounted(() => {
      loadUsers()
    })
    
    return {
      loading,
      userList,
      filterParams,
      pagination,
      userStats,
      dialogVisible,
      dialogMode,
      currentUser,
      ticketDialogVisible,
      ticketDialogUser,
      ticketBalance,
      ticketSubmitting,
      ticketForm,
      
      handleFilterChange,
      handleSearch,
      handleSortChange,
      resetFilters,
      handleSizeChange,
      handleCurrentChange,
      handleViewDetails,
      handleCreateUser,
      handleEditUser,
      handleToggleStatus,
      handleSendMessage,
      handleGrantTickets,
      submitGrantTickets,
      handleExportUsers,
      handleDialogSuccess,
      
      getMemberTypeLabel,
      getMemberTypeTag,
      getStatusLabel,
      getStatusTagType,
      getActivityColor,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

/* 复用活动管理页面的样式 */
.page-header,
.filter-section,
.stats-section,
.list-section,
.table-header,
.pagination-section {
  margin-bottom: 24px;
}

.stat-card,
.filter-card {
  border: none;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  font-size: 24px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(64, 158, 255, 0.1);
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.stat-title {
  font-size: 14px;
  color: #909399;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #303133;
}

.user-contact {
  font-size: 12px;
  color: #909399;
}

.score-text {
  font-size: 12px;
  color: #606266;
  margin-left: 8px;
}

.expand-content {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-info {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .user-details {
    align-items: center;
  }
}

/* 数据卡片响应式布局优化 */
@media (max-width: 1200px) {
  .stats-section .el-col {
    margin-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .stats-section .el-col {
    margin-bottom: 12px;
  }
}

@media (max-width: 480px) {
  .stats-section .el-col {
    margin-bottom: 8px;
  }
}

/* 赠送单次发起券 */
.ticket-user-line {
  margin: 0 0 16px;
  color: #606266;
}

.ticket-balance {
  margin-left: 12px;
  color: #e6a23c;
  font-size: 13px;
}

.ticket-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
</style>